import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Map, Swords, Scroll, Crown, Sparkles, Hand, Shield, Star, Tent, Flag, Compass, ChevronRight, Skull, Heart, Eye, Flame, Trophy, Lock, HelpCircle, TrendingUp, HandCoins } from "lucide-react";
import { GoldCoin, FollisCoin } from "@/components/CoinIcons";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { PageHeader, Loader, btn } from "@/components/Page";
import Modal from "@/components/Modal";
import { useAuth } from "@/context/AuthContext";
import BeastIcon from "@/components/BeastIcon";

// Custom Campaign Icon for main page header only
const CampaignIcon = ({ size = 24, className = "" }) => (
  <img 
    src="/campaign-icon.png" 
    alt="Imperial Campaign" 
    className={`inline-block ${className}`}
    style={{ 
      width: size * 1.2,
      height: size * 1.2,
      objectFit: 'contain'
    }}
  />
);

const TYPE_ICON = { arena: Swords, beast: () => <BeastIcon size={16} />, encounter: Scroll, hidden: HelpCircle, boss: Crown };
const TYPE_COLOR = {
  arena: "text-amber-400 border-amber-800/50", beast: "text-orange-400 border-orange-800/50",
  encounter: "text-sky-400 border-sky-800/50", hidden: "text-zinc-400 border-zinc-700",
  boss: "text-rose-400 border-rose-700/60"};

function Stars({ n }) {
  return <span className="inline-flex">{[1, 2, 3, 4, 5].map((i) => <Star key={i} size={13} className={i <= n ? "text-amber-400 fill-amber-400" : "text-zinc-700"} />)}</span>;
}

function RewardLine({ rewards }) {
  if (!rewards) return null;
  const r = rewards;
  return (
    <div className="flex flex-wrap gap-2 mt-3" data-testid="campaign-rewards">
      {r.fame ? <span className="flex items-center gap-1 text-sm text-violet-300 border border-violet-800/40 bg-violet-950/20 px-2 py-0.5 rounded-sm"><Sparkles size={13} /> +{r.fame} Fame</span> : null}
      {r.denarii ? <span className="flex items-center gap-1 text-sm text-slate-200 border border-slate-500/40 bg-slate-700/20 px-2 py-0.5 rounded-sm"><HandCoins size={13} /> +{r.denarii} Denarii</span> : null}
      {r.rep ? <span className={`flex items-center gap-1 text-sm px-2 py-0.5 rounded-sm border ${r.rep > 0 ? "text-emerald-300 border-emerald-800/40 bg-emerald-950/20" : "text-rose-300 border-rose-800/40 bg-rose-950/20"}`}><Flag size={13} /> {r.rep > 0 ? "+" : ""}{r.rep} Rep</span> : null}
      {r.collectible ? <span className="flex items-center gap-1 text-sm text-teal-300 border border-teal-800/40 bg-teal-950/20 px-2 py-0.5 rounded-sm"><Trophy size={13} /> {r.collectible}</span> : null}
      {r.revealed ? <span className="flex items-center gap-1 text-sm text-sky-300 border border-sky-800/40 bg-sky-950/20 px-2 py-0.5 rounded-sm"><Eye size={13} /> Discovered: {r.revealed}</span> : null}
      {r.title ? <span className="flex items-center gap-1 text-sm text-amber-200 border border-amber-600/50 bg-amber-900/30 px-2 py-0.5 rounded-sm"><Crown size={13} /> Title: {r.title}</span> : null}
      {r.cosmetic ? <span className="flex items-center gap-1 text-sm text-amber-200 border border-amber-600/50 bg-amber-900/30 px-2 py-0.5 rounded-sm"><Sparkles size={13} /> Cosmetic unlocked!</span> : null}
      {r.injury ? <span className="flex items-center gap-1 text-sm text-rose-300 border border-rose-800/40 bg-rose-950/20 px-2 py-0.5 rounded-sm"><Heart size={13} /> Injury −{r.injury}%</span> : null}
      {r.set_complete ? <span className="flex items-center gap-1 text-sm text-amber-200 border border-amber-600/50 bg-amber-900/30 px-2 py-0.5 rounded-sm"><Trophy size={13} /> {r.set_complete} relics complete!</span> : null}
    </div>
  );
}

export default function Campaign() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [data, setData] = useState(null);     // province view (nodes + expedition)
  const [overview, setOverview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [squadPicks, setSquadPicks] = useState([]);
  const [node, setNode] = useState(null);     // node modal: {def, prep|encounter|result}
  const [tactics, setTactics] = useState("balanced");
  const [campOpen, setCampOpen] = useState(false);
  const [confirmStart, setConfirmStart] = useState(false);
  const [confirmAbandon, setConfirmAbandon] = useState(false);
  const [restPrompt, setRestPrompt] = useState(null);  // node pending a "rest first?" prompt
  const [confirmRest, setConfirmRest] = useState(false);  // gold-spend confirm for stamina recovery
  const [restDontAsk, setRestDontAsk] = useState(() => localStorage.getItem("campaign_rest_no_confirm") === "1");
  const [restModalDontAsk, setRestModalDontAsk] = useState(false);
  const [province, setProvince] = useState("italia");  // currently viewed province
  const [view, setView] = useState("campaign");        // sub-page: campaign | trophy | chronicle
  const [boostBusy, setBoostBusy] = useState(false);
  const [boostDone, setBoostDone] = useState(null);    // {gladiator_name, boosts_used, boosts_max} after assigning
  const [legOpen, setLegOpen] = useState(false);       // legendary Greece capstone claim modal
  const [legWpn, setLegWpn] = useState(null);
  const [legWpnCls, setLegWpnCls] = useState("");
  const [legWpnRer, setLegWpnRer] = useState(3);
  const [legArm, setLegArm] = useState(null);
  const [legArmRer, setLegArmRer] = useState(3);
  const [legBusy, setLegBusy] = useState(false);
  const [potionCount, setPotionCount] = useState(0);
  const [showPotionConfirm, setShowPotionConfirm] = useState(false);

  const assignBoost = async (gladiator_id) => {
    setBoostBusy(true);
    try {
      const { data: r } = await api.post("/campaign/training-boost", { gladiator_id });
      setBoostDone(r);
      toast.success(`${r.gladiator_name} gains +10% training for 7 days!`);
      loadOverview();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBoostBusy(false); }
  };

  const useStaminaPotion = async () => {
    setShowPotionConfirm(false);
    setBusy(true);
    try {
      const { data: r } = await api.post("/campaign/stamina/use-potion");
      setData(r);
      toast.success(r.camp_result || "Stamina restored!");
      setPotionCount(r.remaining_potions || 0);
      refreshUser();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const loadOverview = () => api.get("/campaign").then((r) => setOverview(r.data)).catch((e) => toast.error(apiErr(e)));
  const loadProvince = (pid) => api.get(`/campaign/province/${pid || province}`).then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
  const loadPotionCount = () => api.get("/inventory").then((r) => {
    const potions = r.data.consumables?.find(c => c.id === 'campaign_stamina_potion');
    setPotionCount(potions?.count || 0);
  }).catch(() => {});

  useEffect(() => { loadOverview(); loadPotionCount(); }, []);

  // Drive the viewed province off the active expedition; otherwise the first unlocked, unconquered one.
  useEffect(() => {
    if (!overview) return;
    const active = overview.expedition?.province;
    const pid = active || overview.provinces.find((p) => p.unlocked && !p.completed)?.id
              || overview.provinces.find((p) => p.unlocked)?.id || "italia";
    setProvince(pid);
    loadProvince(pid);
  }, [overview]);  // eslint-disable-line react-hooks/exhaustive-deps

  const selectProvince = (pid) => { setProvince(pid); loadProvince(pid); };

  const openLegendary = () => {
    const pr = overview?.pending_legendary_reward; if (!pr) return;
    setLegWpn(pr.weapon?.item || null);
    setLegWpnCls(pr.weapon?.cls || "");
    setLegWpnRer(pr.weapon?.rerolls_left ?? 3);
    setLegArm(pr.armor?.item || null);
    setLegArmRer(pr.armor?.rerolls_left ?? 3);
    setLegOpen(true);
  };
  const rollLeg = async (slot) => {
    if (slot === "weapon" && !legWpnCls) return toast.error("Choose a class for the weapon first.");
    setLegBusy(true);
    try {
      const { data: r } = await api.post("/campaign/legendary/roll", slot === "weapon" ? { slot, cls: legWpnCls } : { slot });
      if (slot === "weapon") { setLegWpn(r.item); setLegWpnRer(r.rerolls_left); }
      else { setLegArm(r.item); setLegArmRer(r.rerolls_left); }
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLegBusy(false); }
  };
  const claimLeg = async () => {
    setLegBusy(true);
    try {
      await api.post("/campaign/legendary/claim");
      toast.success("The legendary spoils of Hellas are yours! Find them in your Armory.", { icon: <Crown size={18} className="text-amber-400" /> });
      setLegOpen(false);
      await loadOverview();
      refreshUser && refreshUser();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLegBusy(false); }
  };

  // Resume an interrupted encounter after a page refresh: if the expedition has a pending encounter
  // and no node modal is open, reopen it at its current beat so the player is never stuck.
  useEffect(() => {
    const enc = data?.expedition?.encounter;
    if (enc && !node && !busy) {
      const n = (data.nodes || []).find((x) => x.id === enc.node_id) || { id: enc.node_id };
      enter(n);
    }
  }, [data]);  // eslint-disable-line react-hooks/exhaustive-deps

  const togglePick = (id) => setSquadPicks((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : (cur.length >= 3 ? cur : [...cur, id]));

  const recommendSquad = () => {
    const gs = overview?.gladiators || [];
    const byClass = {};
    gs.forEach((g) => { (byClass[g.cls] = byClass[g.cls] || []).push(g); });
    Object.values(byClass).forEach((arr) => arr.sort((a, b) => (b.rating || 0) - (a.rating || 0)));
    const picks = [];
    // Prioritize one of each gladiator type (covers Italia's terrain spread), strongest per class first.
    Object.keys(byClass).forEach((cls) => { if (picks.length < 3) picks.push(byClass[cls][0].id); });
    if (picks.length < 3) {
      gs.filter((g) => !picks.includes(g.id)).sort((a, b) => (b.rating || 0) - (a.rating || 0))
        .forEach((g) => { if (picks.length < 3) picks.push(g.id); });
    }
    setSquadPicks(picks.slice(0, 3));
  };

  const start = async () => {
    if (squadPicks.length !== 3) return;
    setConfirmStart(false);
    setBusy(true);
    try { const { data } = await api.post("/campaign/expedition/start", { gladiator_ids: squadPicks, province }); setData(data); setSquadPicks([]); loadOverview(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const tryEnter = (n) => {
    if (exp && exp.rested === false) { setRestPrompt(n); return; }
    enter(n);
  };

  const enter = async (n) => {
    setRestPrompt(null);
    setBusy(true); setTactics("balanced");
    try {
      const { data: prep } = await api.post(`/campaign/node/${n.id}/enter`);
      // If an encounter was interrupted (e.g. page refresh), the backend resumes it at its current beat.
      if (prep.kind === "encounter") setNode({ def: n, encounter: prep });
      else setNode({ def: n, prep });
    } catch (e) { toast.error(apiErr(e)); setNode(null); } finally { setBusy(false); }
  };

  const engage = async () => {
    const n = node?.def;
    if (!n) return;
    setBusy(true);
    try {
      const { data: res } = await api.post(`/campaign/node/${n.id}/engage`, { tactics });
      if (res.kind === "encounter") setNode({ def: n, encounter: res });
      else setNode({ def: n, result: res });
      loadOverview(); loadProvince();
      if (res.kind === "finale") refreshUser && refreshUser();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const choose = async (choiceId) => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/campaign/node/choose", { choice_id: choiceId });
      setNode((prev) => ({ ...prev, encounter: res.next_beat || null, result: res }));
      loadOverview(); loadProvince();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const doCamp = async (activity) => {
    setBusy(true);
    try { const { data } = await api.post("/campaign/camp", { activity }); setData(data); if (data.camp_result) toast.success(data.camp_result); setCampOpen(false); loadOverview(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const buyConsumable = async (item) => {
    setBusy(true);
    try { const { data } = await api.post(`/campaign/consumable/${item}`); setData(data); if (data.camp_result) toast.success(data.camp_result); loadOverview(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const recoverStamina = async () => {
    setBusy(true);
    try { const { data } = await api.post("/campaign/stamina/recover"); setData(data); if (data.camp_result) toast.success(data.camp_result); refreshUser && refreshUser(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const onRestClick = () => { if (restDontAsk) recoverStamina(); else { setRestModalDontAsk(false); setConfirmRest(true); } };
  const confirmRestNow = () => {
    if (restModalDontAsk) { localStorage.setItem("campaign_rest_no_confirm", "1"); setRestDontAsk(true); }
    setConfirmRest(false);
    recoverStamina();
  };

  const buyUpgrade = async (id) => {
    setBusy(true);
    try { const { data } = await api.post(`/campaign/upgrade/${id}`); setOverview(data); toast.success("Upgrade acquired!"); loadProvince(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const abandon = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/campaign/expedition/abandon");
      setOverview(data);
      setConfirmAbandon(false);
      toast.success("Expedition abandoned. The province resets — banked rewards are kept, escrow forfeited.");
      loadProvince();
    }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (!data || !overview) return <Loader />;
  const exp = data.expedition;
  const provName = data.name || "Italia";

  return (
    <div className="space-y-6" data-testid="campaign-page">
      <PageHeader title="Imperial Campaign" subtitle="The Emperor expands Rome. Lead your ludus across the provinces. Progress is permanent." icon={CampaignIcon} />

      {/* Sub-page tabs */}
      <div className="flex gap-1 border-b border-zinc-800" data-testid="campaign-tabs">
        {[["campaign", "Campaign", Map], ["trophy", "Trophy Hall", Trophy], ["chronicle", "The Chronicle", Scroll]].map(([id, label, Icon]) => (
          <button key={id} data-testid={`tab-${id}`} onClick={() => setView(id)}
            className={`px-4 py-2 text-sm font-cinzel uppercase tracking-wide flex items-center gap-2 border-b-2 -mb-px transition-colors ${
              view === id ? "border-amber-500 text-amber-300" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {overview.pending_legendary_reward && (() => {
        const pr = overview.pending_legendary_reward;
        const rl = (pr.rarity || "legendary");
        const rlab = rl.charAt(0).toUpperCase() + rl.slice(1);
        return (
        <button data-testid="legendary-reward-banner" onClick={openLegendary}
          className="w-full mt-4 flex items-center gap-3 border border-amber-500/60 bg-gradient-to-r from-amber-950/40 to-zinc-950 px-4 py-3 rounded-sm hover:border-amber-400 transition-colors text-left">
          <Crown size={20} className="text-amber-400 shrink-0 animate-pulse" />
          <span className="text-sm text-amber-100">
            <span className="font-cinzel uppercase tracking-wide text-amber-300">Spoils of {pr.province_name} await!</span>
            {" "}For conquering {pr.province_name}, claim {(/^[aeiou]/i.test(rlab) ? "an" : "a")} <span className="font-semibold">{rlab} weapon</span> and {(/^[aeiou]/i.test(rlab) ? "an" : "a")} <span className="font-semibold">{rlab} armor</span> — roll their affixes to taste. <span className="underline">Claim now →</span>
          </span>
        </button>);
      })()}

      {view === "campaign" && (<>
      {/* Province selector — Italia, Gaul, ... (sequential unlock) */}
      <div className="flex flex-wrap gap-2" data-testid="province-selector">
        {overview.provinces.map((pv) => {
          const active = pv.id === province;
          const expActive = !!overview.expedition;
          const disabled = !pv.unlocked || (expActive && overview.expedition.province !== pv.id);
          return (
            <button key={pv.id} data-testid={`province-tab-${pv.id}`}
              onClick={() => !disabled && selectProvince(pv.id)} disabled={disabled}
              title={!pv.unlocked ? "Locked — conquer the prior province first" : ""}
              className={`px-4 py-2 rounded-sm border text-sm font-cinzel transition-colors flex items-center gap-2 ${
                active ? "border-amber-500 bg-amber-900/20 text-amber-200"
                : disabled ? "border-zinc-900 bg-zinc-900/20 text-zinc-600 cursor-not-allowed"
                : "border-zinc-800 bg-zinc-900/40 text-zinc-300 hover:border-zinc-600"}`}>
              {!pv.unlocked ? <Lock size={13} /> : pv.completed ? <Crown size={13} className="text-amber-400" /> : <Map size={13} />}
              {pv.name}
              {pv.completed && pv.unlocked && <span className="text-[10px] uppercase tracking-widest text-emerald-400">✓</span>}
            </button>
          );
        })}
      </div>

      {/* Persistent pending training-boost reward (survives navigating away from the finale screen) */}
      {overview.pending_training_boost?.eligible?.length > 0 && (
        <div data-testid="pending-training-boost" className="mb-4 border border-emerald-700/50 bg-emerald-950/20 rounded-sm p-4">
          <div className="flex items-center gap-2 text-emerald-300 font-cinzel flex-wrap">
            <TrendingUp size={16} /> Veteran's Edge — +10% Training for 7 Days
            {overview.pending_training_boost.province_name && (
              <span className="text-[10px] uppercase tracking-widest text-emerald-500/70">from {overview.pending_training_boost.province_name}</span>
            )}
          </div>
          <p className="text-xs text-zinc-400 mt-1">Assign this reward to one eligible gladiator. Each fighter can be boosted at most {overview.pending_training_boost.eligible[0]?.boosts_max ?? 5} times in their career.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {overview.pending_training_boost.eligible.map((e) => (
              <button key={e.id} data-testid={`pending-boost-pick-${e.id}`} disabled={boostBusy} onClick={() => assignBoost(e.id)}
                className="flex items-center gap-2 px-3 py-2 border border-zinc-700 hover:border-emerald-500 hover:bg-emerald-950/30 rounded-sm transition-colors disabled:opacity-50">
                <span className="text-sm text-zinc-100">{e.name}</span>
                <span className="text-[11px] text-zinc-500 tabular-nums">{e.boosts_used}/{e.boosts_max} used</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Fame + province summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="border border-violet-900/40 bg-zinc-950 p-4 rounded-sm" data-testid="campaign-fame">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5"><Sparkles size={12} /> Fame</div>
          <div className="text-2xl font-cinzel text-violet-300 mt-1">{overview.fame}</div>
          <div className="text-xs text-zinc-500">{overview.title}</div>
          <div className="mt-2 pt-2 border-t border-zinc-800 flex items-center gap-1.5 text-sm text-slate-200" data-testid="campaign-denarii">
            <HandCoins size={13} /> <b>{overview.denarii}</b> <span className="text-xs text-zinc-500">Denarii</span>
          </div>
        </div>
        <div className="border border-emerald-900/40 bg-zinc-950 p-4 rounded-sm" data-testid="campaign-reputation">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5"><Flag size={12} /> Reputation</div>
          <div className={`text-2xl font-cinzel mt-1 ${data.reputation > 0 ? "text-emerald-300" : data.reputation < 0 ? "text-rose-300" : "text-zinc-300"}`}>
            {data.reputation > 0 ? "+" : ""}{data.reputation}
          </div>
          <div className="text-xs text-zinc-500">{data.reputation >= 40 ? `Beloved in ${provName}` : data.reputation >= 10 ? "Respected" : data.reputation > 0 ? "Favorable" : data.reputation === 0 ? "Neutral standing" : data.reputation <= -40 ? "Feared & hated" : data.reputation <= -10 ? "Disliked" : "Mistrusted"}</div>
        </div>
        <div className="border border-amber-900/40 bg-zinc-950 p-4 rounded-sm">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5"><Flag size={12} /> {provName} Standing</div>
          <div className="mt-1"><Stars n={data.reputation_stars} /></div>
          <div className="text-xs text-zinc-500">{data.completion_pct}% conquered</div>
        </div>
        <div className="border border-teal-900/40 bg-zinc-950 p-4 rounded-sm">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5"><Trophy size={12} /> Collection</div>
          <div className="text-2xl font-cinzel text-teal-300 mt-1">{data.collectibles.length}/{data.collectibles_total}</div>
          <div className="text-xs text-zinc-500 truncate">{data.collectible_names.join(" · ") || "None yet"}</div>
          {data.endings_total > 0 && (
            <div data-testid="endings-tracker" className="mt-2 pt-2 border-t border-zinc-800 flex items-center gap-1.5 text-xs text-amber-300/90"
              title="Different choices lead to different endings — each grants a unique cosmetic. Replay with a fresh squad to collect them all.">
              <Crown size={12} className="text-amber-400" /> Endings collected: <b>{data.endings_collected}/{data.endings_total}</b>
            </div>
          )}
        </div>
      </div>

      {/* Expedition HUD or start */}
      {!exp ? (
        <div className="border border-amber-900/30 bg-zinc-950 p-5 rounded-sm max-w-2xl" data-testid="expedition-start">
          <p className="text-sm text-zinc-400 mb-1">Form a <b className="text-amber-300">squad of exactly 3 gladiators</b> to march into <b className="text-amber-300">{provName}</b>. Their strengths combine, pick 3 different classes and cover your weaknesses. Campaign fatigue and injuries stay in the campaign; your PvP record is untouched.</p>
          {data.completed && (
            <p data-testid="replay-note" className="text-xs text-teal-300/90 mt-2 flex items-start gap-1.5 border-l-2 border-teal-800/50 pl-2.5">
              <Crown size={13} className="shrink-0 mt-0.5 text-amber-400" />
              <span>You've conquered {provName}. Replay it with a fresh squad to chase a different ending and cosmetic{data.endings_total > 0 ? <> (<b className="text-amber-300">{data.endings_collected}/{data.endings_total} endings collected</b>)</> : ""}. A gladiator who already conquered it can't return, and every completed replay earns another capstone weapon and armor.</span>
            </p>
          )}
          <div className="flex items-center justify-between mt-4 mb-2">
            <label className="text-[11px] uppercase tracking-widest text-zinc-500">Choose your squad</label>
            <div className="flex items-center gap-3">
              <button data-testid="recommend-squad-btn" onClick={recommendSquad} disabled={busy || overview.gladiators.length < 1}
                className="text-[11px] uppercase tracking-widest text-amber-400 hover:text-amber-300 flex items-center gap-1 transition-colors disabled:opacity-40">
                <Sparkles size={12} /> Recommend
              </button>
              <span className="text-[11px] uppercase tracking-widest text-amber-400" data-testid="squad-count">{squadPicks.length}/3 selected</span>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1">
            {overview.gladiators.map((g) => {
              const sel = squadPicks.includes(g.id);
              const done = (g.provinces_done || []).includes(province);   // already conquered this province
              const full = (squadPicks.length >= 3 && !sel) || done;
              return (
                <button key={g.id} data-testid={`squad-pick-${g.id}`} onClick={() => !done && togglePick(g.id)} disabled={busy || full}
                  title={done ? `${g.name} has already conquered ${provName} and can't return.` : ""}
                  className={`text-left px-3 py-2.5 border rounded-sm transition-colors flex items-center justify-between gap-2 ${
                    sel ? "border-amber-500 bg-amber-900/20" : full ? "border-zinc-900 bg-zinc-900/20 opacity-40 cursor-not-allowed" : "border-zinc-800 bg-zinc-900/40 hover:border-zinc-600"}`}>
                  <span className="text-sm text-zinc-100">{g.name} <span className="text-xs text-zinc-500">· {g.cls}</span></span>
                  {sel ? <span className="text-[10px] uppercase tracking-widest text-amber-400">✓ in squad</span>
                       : done ? <span className="text-[10px] uppercase tracking-widest text-teal-500/80">conquered</span>
                       : <span className="text-[10px] uppercase tracking-widest text-zinc-600">R {g.rating}</span>}
                </button>
              );
            })}
          </div>
          <button data-testid="embark-btn" onClick={() => setConfirmStart(true)} disabled={busy || squadPicks.length !== 3} className={btn() + " w-full mt-4 flex items-center justify-center gap-2"}>
            <Compass size={16} /> {squadPicks.length !== 3 ? `Select ${3 - squadPicks.length} more` : "Begin Expedition"}
          </button>
        </div>
      ) : (
        <div className="border border-zinc-800 bg-zinc-950 p-4 rounded-sm flex flex-wrap items-center gap-4 justify-between" data-testid="expedition-hud">
          <div className="flex items-center gap-3">
            <Shield size={18} className="text-amber-500 shrink-0" />
            <div>
              <div className="flex flex-wrap gap-1.5" data-testid="squad-chips">
                {(exp.squad || []).map((m) => (
                  <span key={m.id} className="text-xs text-zinc-200 border border-zinc-700 bg-zinc-900 px-2 py-0.5 rounded-sm">{m.name}{m.cls ? <span className="text-zinc-500"> · {m.cls}</span> : null}</span>
                ))}
              </div>
              <div className="text-[11px] text-zinc-500 mt-1">squad on campaign in {provName}</div>
              {exp.next_checkpoint ? (
                <div data-testid="hud-next-checkpoint" className="text-[11px] text-amber-400/90 mt-1 flex items-center gap-1.5">
                  <Flag size={11} /> Next reward bank: <b className="text-amber-300">{exp.next_checkpoint.title}</b>
                </div>
              ) : (
                <div className="text-[11px] text-emerald-400/80 mt-1 flex items-center gap-1.5"><Flag size={11} /> All rewards banked</div>
              )}
              {exp.escrow_preview?.count > 0 && (
                <div data-testid="hud-escrow" className="text-[11px] text-slate-300/90 mt-1 flex items-center gap-1.5"
                  title="Rewards held in escrow. Reach the next Bank Reward point to secure them — abandon first and they're forfeited.">
                  <HandCoins size={11} className="text-slate-300" /> Held until you bank:
                  {exp.escrow_preview.fame ? <span className="text-violet-300"> +{exp.escrow_preview.fame} Fame</span> : null}
                  {exp.escrow_preview.denarii ? <span className="text-slate-200"> +{exp.escrow_preview.denarii} Denarii</span> : null}
                  {exp.escrow_preview.collectibles ? <span className="text-teal-300"> · {exp.escrow_preview.collectibles} relic{exp.escrow_preview.collectibles > 1 ? "s" : ""}</span> : null}
                </div>
              )}
              {overview.collection_bonuses?.length > 0 && (
                <div className="text-[11px] text-teal-400/90 mt-1 flex items-center gap-1.5" data-testid="hud-relic-bonuses">
                  <Trophy size={11} /> Relics active: {overview.collection_bonuses.join(" · ")}
                </div>
              )}
            </div>
          </div>
          <div className="min-w-[180px]">
            <div className="flex justify-between text-[10px] uppercase tracking-widest text-zinc-500 mb-1"><span>Stamina</span><span>{exp.stamina}/{exp.stamina_max}</span></div>
            <div className="h-2 bg-zinc-800 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-amber-600 to-amber-400" style={{ width: `${(exp.stamina / exp.stamina_max) * 100}%` }} data-testid="stamina-bar" /></div>
            <div className="text-[10px] text-zinc-600 mt-1">+5/hr · <span data-testid="campaign-injuries-count" className={exp.injuries.length > 0 ? "text-orange-400 font-semibold" : "text-orange-400/70"}>injuries: {exp.injuries.length}</span></div>
            {data.stamina_recover && (
              <button data-testid="recover-stamina-btn" onClick={onRestClick}
                disabled={busy || exp.stamina >= exp.stamina_max || (user?.gold ?? 0) < data.stamina_recover.cost || data.stamina_recover.remaining <= 0}
                title={exp.stamina >= exp.stamina_max ? "Stamina already full" : data.stamina_recover.remaining <= 0 ? "Daily limit reached" : (user?.gold ?? 0) < data.stamina_recover.cost ? "Not enough Gold" : ""}
                className="mt-2 w-full text-[11px] uppercase tracking-wide flex items-center justify-center gap-1.5 px-2 py-1.5 border border-amber-700/50 text-amber-300 hover:bg-amber-900/20 rounded-sm transition-colors disabled:opacity-40 disabled:hover:bg-transparent">
                <Sparkles size={12} /> +{data.stamina_recover.amount} Stamina · {data.stamina_recover.cost} Gold
                <span className="text-zinc-500 normal-case tracking-normal">({data.stamina_recover.remaining}/{data.stamina_recover.cap} left)</span>
              </button>
            )}
            {potionCount > 0 && (
              <button
                onClick={() => setShowPotionConfirm(true)}
                disabled={busy || exp.stamina >= exp.stamina_max}
                title={exp.stamina >= exp.stamina_max ? "Stamina already full" : `Use Campaign Stamina Potion (+50 stamina)`}
                className="mt-2 w-full text-[11px] uppercase tracking-wide flex items-center justify-center gap-1.5 px-2 py-1.5 border border-amber-700/50 text-amber-300 hover:bg-amber-900/20 rounded-sm transition-colors disabled:opacity-40 disabled:hover:bg-transparent"
              >
                <img src="/campaign-stamina-v2.png" alt="Potion" className="w-4 h-4" /> +50 Stamina · Potion ({potionCount})
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button data-testid="camp-btn" onClick={() => setCampOpen(true)} disabled={busy} className={btn("ghost") + " flex items-center gap-2"}><Tent size={15} /> Camp</button>
            <button data-testid="abandon-btn" onClick={() => setConfirmAbandon(true)} disabled={busy} className="px-3 py-2 text-sm border border-zinc-700 text-zinc-400 hover:border-rose-700 hover:text-rose-300 rounded-sm transition-colors">Cancel Province</button>
          </div>
        </div>
      )}

      {/* Quartermaster — permanent, campaign-wide upgrades bought with Denarii */}
      <div className="border border-zinc-800 bg-zinc-950 rounded-sm p-4" data-testid="quartermaster">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-cinzel text-amber-300 uppercase tracking-wide text-lg flex items-center gap-2"><Tent size={18} /> Quartermaster</h2>
          <span className="text-[11px] uppercase tracking-widest text-zinc-500 flex items-center gap-1"><HandCoins size={12} className="text-slate-300" /> {overview.denarii} Denarii · <span className={overview.price_mult < 1 ? "text-emerald-400" : overview.price_mult > 1 ? "text-rose-400" : "text-zinc-400"}>{overview.price_label}</span></span>
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          {(overview.upgrades || []).map((u) => (
            <div key={u.id} data-testid={`upgrade-${u.id}`} className={`border rounded-sm p-3 flex flex-col ${u.owned ? "border-emerald-800/40 bg-emerald-950/10" : "border-zinc-800 bg-zinc-900/30"}`}>
              <div className="text-sm font-cinzel text-zinc-100">{u.name}</div>
              <div className="text-xs text-zinc-500 mt-1 flex-1">{u.desc}</div>
              {u.owned ? (
                <div className="mt-3 text-[11px] uppercase tracking-widest text-emerald-400 flex items-center gap-1"><Star size={12} className="fill-emerald-400" /> Acquired</div>
              ) : (
                <button data-testid={`buy-upgrade-${u.id}`} onClick={() => buyUpgrade(u.id)} disabled={busy || overview.denarii < u.effective_cost}
                  className={btn() + " mt-3 w-full text-xs flex items-center justify-center gap-1.5 disabled:opacity-40"}>
                  <HandCoins size={13} /> {u.effective_cost} Denarii
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Road map — part of the Campaign sub-page */}
      <div>
        <h2 className="font-cinzel text-amber-300 uppercase tracking-wide text-lg mb-3 flex items-center gap-2"><Map size={18} /> {provName} — The Road</h2>
        <div data-testid="province-completion-rewards" className="mb-3 border border-amber-900/40 bg-amber-950/10 px-3.5 py-3 rounded-sm">
          <div className="text-[10px] uppercase tracking-widest text-amber-400">Province completion spoils</div>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <span data-testid="province-completion-gold" className="inline-flex items-center gap-1 border border-amber-700/50 bg-amber-950/30 px-2 py-1 text-amber-200"><GoldCoin size={13} /> +{data.completion_rewards?.gold || 0} Gold</span>
            <span data-testid="province-completion-training" className="inline-flex items-center gap-1 border border-emerald-800/50 bg-emerald-950/20 px-2 py-1 text-emerald-200"><TrendingUp size={13} /> +{data.completion_rewards?.training_boost_pct || 10}% training · {data.completion_rewards?.training_boost_days || 7} days</span>
            {data.completion_rewards?.capstone_rarity && <span data-testid="province-completion-capstone" className="inline-flex items-center gap-1 border border-violet-800/50 bg-violet-950/20 px-2 py-1 capitalize text-violet-200"><Crown size={13} /> First conquest: {data.completion_rewards.capstone_rarity} weapon &amp; armor</span>}
            <span data-testid="province-completion-ending" className="inline-flex items-center gap-1 border border-sky-800/50 bg-sky-950/20 px-2 py-1 text-sky-200"><Scroll size={13} /> {data.completion_rewards?.ending_rewards}</span>
          </div>
        </div>
        <div data-testid="checkpoint-explainer" className="mb-3 flex items-start gap-2.5 border border-amber-900/40 bg-amber-950/10 px-3.5 py-2.5 rounded-sm text-xs text-zinc-300">
          <Flag size={14} className="text-amber-400 shrink-0 mt-0.5" />
          <span><b className="text-amber-300 uppercase tracking-wide">Bank Reward points:</b> rewards you earn along the road are <b>held in escrow</b> until you clear the next <span className="text-amber-300">Bank Reward point</span> (marked <Flag size={10} className="inline text-amber-400" /> on the map), which secures them for good. This is <b>not</b> a progress save — if you abandon before banking, every reward earned since your last bank is <b className="text-rose-300">forfeited</b> and the province restarts. So reach a Bank Reward point before you leave.</span>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="campaign-map">
          {data.nodes.map((n) => {
            const Icon = TYPE_ICON[n.type] || Swords;
            const masked = n.status === "locked" || n.status === "hidden";  // undiscovered — keep it a mystery
            const cleared = n.status === "cleared";
            const canEnter = exp && n.status === "available" && !busy;
            return (
              <button key={n.id} data-testid={`node-${n.id}`} disabled={!canEnter}
                onClick={() => canEnter && tryEnter(n)}
                className={`text-left p-4 border rounded-sm transition-colors ${masked ? "border-zinc-800" : (TYPE_COLOR[n.type] || "")} ${
                  cleared ? "bg-emerald-950/15 border-emerald-800/40 opacity-80"
                  : masked ? "bg-zinc-900/30 opacity-60 cursor-not-allowed"
                  : canEnter ? "bg-zinc-900/60 hover:bg-zinc-900 hover:border-amber-600 cursor-pointer"
                  : "bg-zinc-900/40 cursor-not-allowed"}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className={`flex items-center gap-2 text-sm font-cinzel ${n.type === "boss" ? "text-rose-300" : masked ? "text-zinc-500" : "text-zinc-200"}`}>
                    {masked ? <HelpCircle size={15} className="text-zinc-600" /> : <Icon size={15} />}
                    {n.title}
                  </span>
                  {cleared && <span className="text-[10px] uppercase tracking-widest text-emerald-400">✓</span>}
                  {n.status === "available" && <ChevronRight size={15} className="text-zinc-600" />}
                </div>
                {n.checkpoint && (
                  <div data-testid={`node-checkpoint-${n.id}`} className={`mt-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-sm border w-fit ${cleared ? "text-emerald-300 border-emerald-700/50 bg-emerald-950/30" : "text-amber-300 border-amber-600/50 bg-amber-950/30"}`}
                    title={cleared ? "Rewards banked here — everything you earned up to this point is secured." : "Bank Reward point: clearing this location banks every reward earned since your last bank. Leave before reaching it and those rewards are forfeited."}>
                    <Flag size={11} /> {cleared ? "Rewards banked" : "Bank Reward"}
                  </div>
                )}
                <p className="text-xs text-zinc-500 mt-1.5 line-clamp-2 italic">{n.desc}</p>
                <div className="flex items-center gap-2 mt-2 text-[10px] uppercase tracking-widest text-zinc-600">
                  {!masked && <span className="capitalize">{n.type === "hidden" && n.title.includes("Shrine") ? "Shrine" : n.type}</span>}
                  {!masked && n.terrain_name && <span className="text-sky-600/80">· {n.terrain_name}</span>}
                  {n.sp ? <span>{!masked ? "· " : ""}{n.sp} stamina</span> : null}
                  {!masked && n.collectible && <span className="text-teal-500/80">· {n.collectible}</span>}
                </div>
              </button>
            );
          })}
        </div>
      </div>
      </>)}

      {/* Trophy Hall sub-page */}
      {view === "trophy" && (
      <div className="space-y-6">
      {overview.trophy_hall?.map((th) => (
        <div key={th.province} className="border border-zinc-800 bg-zinc-950 rounded-sm p-4" data-testid="trophy-hall">
          <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
            <h2 className="font-cinzel text-teal-300 uppercase tracking-wide text-lg flex items-center gap-2"><Trophy size={18} /> Trophy Hall — {th.name}</h2>
            <span className={`text-[11px] uppercase tracking-widest ${th.complete ? "text-amber-300" : "text-zinc-500"}`}>{th.owned_count}/{th.total} relics{th.complete ? " · SET COMPLETE" : ""}</span>
          </div>
          <p className="text-xs text-zinc-500 mb-3">{th.complete ? <span className="text-amber-300">{th.set_bonus} — active.</span> : <>Collect all {th.total} to unlock <span className="text-teal-400">{th.set_bonus}</span>. Each relic grants a small campaign-only buff.</>}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {th.items.map((it) => (
              <div key={it.id} data-testid={`relic-${it.id}`}
                className={`border rounded-sm p-3 ${it.owned ? "border-teal-800/40 bg-teal-950/10" : "border-zinc-800 bg-zinc-900/20"}`}>
                <div className="flex items-center gap-2">
                  {it.owned ? <Trophy size={15} className="text-teal-400 shrink-0" /> : <Lock size={14} className="text-zinc-600 shrink-0" />}
                  <span className={`text-sm font-cinzel ${it.owned ? "text-zinc-100" : "text-zinc-600 italic"}`}>{it.owned ? it.name : "Undiscovered"}</span>
                </div>
                {it.owned ? (
                  <>
                    <div className="text-[10px] uppercase tracking-widest text-teal-400 mt-2">{it.buff}</div>
                    <div className="text-xs text-zinc-500 mt-1 leading-snug">{it.lore}</div>
                  </>
                ) : (
                  <div className="text-xs text-zinc-600 mt-2 italic">A relic of {th.name} waits to be claimed.</div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      </div>
      )}

      {/* The Chronicle sub-page */}
      {view === "chronicle" && (
      <div className="border border-zinc-800 bg-zinc-950 rounded-sm p-4" data-testid="chronicle">
        <h2 className="font-cinzel text-amber-300 uppercase tracking-wide text-lg mb-1 flex items-center gap-2"><Scroll size={18} /> The Chronicle</h2>
        {overview.chronicle?.length > 0 ? (
          <div className="space-y-2 mt-3">
            {overview.chronicle.map((c, i) => (
              <div key={i} data-testid={`chronicle-entry-${i}`} className="border border-zinc-800 bg-zinc-900/30 rounded-sm p-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className={`font-cinzel text-sm ${c.ending === "champion" ? "text-amber-300" : c.ending === "tyrant" ? "text-rose-300" : "text-sky-300"}`}>{c.province_name}: {c.ending_name}</span>
                  <span className="text-[10px] uppercase tracking-widest text-zinc-600">{(c.date || "").slice(0, 10)}</span>
                </div>
                <p className="text-xs text-zinc-500 italic mt-1">{c.blurb}</p>
                <div className="text-[11px] text-zinc-600 mt-1.5">Title: <span className="text-amber-400/80">{c.title}</span>{c.squad?.length ? <span> · Led by {c.squad.join(", ")}</span> : null}</div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-zinc-500 mt-2">Your legend is unwritten. Conquer a province and face its finale to forge an ending — recorded here forever. Replay with new gladiators to write a different fate.</p>
        )}
      </div>
      )}

      {/* Node modal */}
      <Modal open={!!node} onClose={() => { setNode(null); }} title={node?.def?.title || "Campaign"} wide>
        {node && (
          <div className="space-y-3" data-testid="node-modal">
            {/* Prep / lore step — shown on arrival before engaging */}
            {node.prep && !node.encounter && !node.result && (
              <div data-testid="node-prep">
                <p className="text-zinc-300 leading-relaxed italic border-l-2 border-amber-800/50 pl-3">{node.prep.lore}</p>
                {node.prep.terrain_name && (
                  <div className="mt-4 border border-sky-900/40 bg-sky-950/10 rounded-sm p-3" data-testid="terrain-panel">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10px] uppercase tracking-widest text-sky-400 flex items-center gap-1.5"><Map size={12} /> {node.prep.terrain_name}</span>
                      <span data-testid="squad-fit" className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm border ${
                        node.prep.squad_fit === "well-suited" ? "text-emerald-300 border-emerald-800/50 bg-emerald-950/20"
                        : node.prep.squad_fit === "poorly-suited" ? "text-rose-300 border-rose-800/50 bg-rose-950/20"
                        : "text-zinc-400 border-zinc-700"}`}>
                        Squad {node.prep.squad_fit} · ×{node.prep.squad_fit_mult}
                      </span>
                    </div>
                    {node.prep.terrain_note && <div className="text-xs text-zinc-500 mt-1.5">{node.prep.terrain_note}</div>}
                  </div>
                )}
                {node.prep.opponent && (
                  <div className="mt-4 border border-zinc-800 bg-zinc-950 rounded-sm p-3" data-testid="opponent-preview">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">You will face</div>
                    <div className="text-sm text-rose-300 font-cinzel">{node.prep.opponent.name} <span className="text-zinc-500">· {node.prep.opponent.cls}</span></div>
                    {node.prep.opponent.mechanic && <div className="text-xs text-zinc-500 mt-1">{node.prep.opponent.mechanic}</div>}
                  </div>
                )}
                {node.prep.opponent && (
                  <div className="mt-4">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Choose your tactics</div>
                    <div className="grid grid-cols-3 gap-2">
                      {["aggressive", "balanced", "defensive"].map((t) => (
                        <button key={t} data-testid={`tactics-${t}`} onClick={() => setTactics(t)}
                          className={`px-2 py-2 text-xs uppercase tracking-wide rounded-sm border transition-colors ${tactics === t ? "border-amber-500 bg-amber-900/20 text-amber-200" : "border-zinc-800 bg-zinc-900/40 text-zinc-400 hover:border-zinc-600"}`}>
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="mt-4 flex items-center justify-between gap-3">
                  <span className="text-xs text-zinc-500">{node.prep.type === "finale" ? `Your legend in ${node.prep.province_name || provName} will be sealed — permanently recorded in your Chronicle.` : <>Costs <b className="text-amber-300">{node.prep.sp}</b> stamina{node.prep.injuries > 0 ? ` · ${node.prep.injuries} injury` : ""}</>}</span>
                  <span className="flex gap-2">
                    <button data-testid="prep-back-btn" onClick={() => setNode(null)} className="px-3 py-2 text-sm border border-zinc-700 text-zinc-400 hover:border-zinc-500 rounded-sm transition-colors">Not yet</button>
                    <button data-testid="engage-btn" onClick={engage} disabled={busy || !node.prep.can_engage} className={btn() + " flex items-center gap-2"}>
                      <Flame size={15} /> {node.prep.type === "finale" ? "Seal Your Legend" : !node.prep.can_engage ? "Too exhausted" : node.prep.opponent ? "Engage" : "Begin"}
                    </button>
                  </span>
                </div>
              </div>
            )}

            {/* Encounter beat */}
            {node.encounter && (
              <div data-testid="encounter-beat">
                <p className="text-zinc-200 leading-relaxed">{node.encounter.text}</p>
                <div className="space-y-2 mt-4">
                  {node.encounter.choices.map((c) => {
                    const repLocked = c.require_rep != null && data.reputation < c.require_rep;
                    return (
                    <button key={c.id} data-testid={`choice-${c.id}`} disabled={busy || repLocked} onClick={() => choose(c.id)}
                      className="w-full text-left px-4 py-3 border border-zinc-800 bg-zinc-900/40 text-zinc-100 hover:border-amber-600 rounded-sm transition-colors flex items-center justify-between gap-3 disabled:opacity-50 disabled:hover:border-zinc-800">
                      <span className="text-sm flex items-center gap-2 flex-wrap">{c.label}
                        {c.cost ? <span className="text-[10px] uppercase tracking-widest text-slate-300/90 flex items-center gap-0.5"><HandCoins size={11} /> {c.cost} Denarii</span> : null}
                        {c.require_rep != null && <span className={`text-[10px] uppercase tracking-widest ${repLocked ? "text-rose-400/80" : "text-emerald-500/80"}`}>· {c.require_rep} rep</span>}
                      </span>
                      <ChevronRight size={15} className="text-zinc-600 shrink-0" />
                    </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Result (fight or encounter outcome) */}
            {node.result && !node.encounter && (
              <div data-testid="node-result">
                {node.result.kind === "finale" ? (
                  <div data-testid="finale-result" className="text-center">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-amber-500/70">The Legend of {node.result.province_name || provName}</div>
                    <h3 className={`mt-2 font-cinzel text-2xl ${node.result.ending === "champion" ? "text-amber-300" : node.result.ending === "tyrant" ? "text-rose-300" : "text-sky-300"}`}>{node.result.ending_name}</h3>
                    <p className="text-sm text-zinc-300 italic mt-1">{node.result.blurb}</p>
                    <p className="text-sm text-zinc-300 leading-relaxed mt-4 text-left border-l-2 border-amber-800/50 pl-3">{node.result.epilogue}</p>
                    <div className="mt-4 inline-flex items-center gap-2 text-sm text-amber-200 border border-amber-600/50 bg-amber-900/20 px-3 py-1.5 rounded-sm">
                      <Crown size={15} /> Title earned: <b>{node.result.title}</b>
                    </div>
                    {node.result.gold_reward ? (
                      <div data-testid="finale-gold-reward" className="mt-3 inline-flex items-center gap-2 text-sm text-amber-300 border border-amber-600/50 bg-amber-950/30 px-3 py-1.5 rounded-sm ml-0 sm:ml-2">
                        <GoldCoin size={15} /> +{node.result.gold_reward} Gold for conquering {node.result.province_name || provName}!
                      </div>
                    ) : null}
                    {node.result.training_boost?.eligible?.length > 0 && (
                      <div data-testid="finale-training-boost" className="mt-5 text-left border border-emerald-700/50 bg-emerald-950/20 rounded-sm p-4">
                        <div className="flex items-center gap-2 text-emerald-300 font-cinzel">
                          <TrendingUp size={16} /> Veteran's Edge — +10% Training for 7 Days
                        </div>
                        {boostDone ? (
                          <p data-testid="boost-assigned" className="text-sm text-emerald-200 mt-2">
                            <b>{boostDone.gladiator_name}</b> now trains +10% faster for a week. ({boostDone.boosts_used}/{boostDone.boosts_max} career boosts used)
                          </p>
                        ) : (
                          <>
                            <p className="text-xs text-zinc-400 mt-1">Reward one gladiator from your victorious squad. Each fighter can be boosted at most 5 times in their career — choose now, this reward is not saved if skipped.</p>
                            <div className="mt-3 flex flex-col gap-2">
                              {node.result.training_boost.eligible.map((e) => (
                                <button key={e.id} data-testid={`boost-pick-${e.id}`} disabled={boostBusy}
                                  onClick={() => assignBoost(e.id)}
                                  className="flex items-center justify-between gap-3 px-3 py-2 border border-zinc-700 hover:border-emerald-500 hover:bg-emerald-950/30 rounded-sm transition-colors disabled:opacity-50">
                                  <span className="text-sm text-zinc-100">{e.name}</span>
                                  <span className="text-[11px] text-zinc-500 tabular-nums">{e.boosts_used}/{e.boosts_max} used</span>
                                </button>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                <>
                {node.result.text && <p className="text-zinc-200 leading-relaxed">{node.result.text}</p>}
                {(node.result.headline || node.result.fight?.headline) && (
                  <p className={`mt-2 font-cinzel ${(node.result.won ?? node.result.fight?.won) ? "text-emerald-300" : "text-rose-300"}`}>
                    {node.result.headline || node.result.fight?.headline}
                  </p>
                )}
                {(node.result.log || node.result.fight?.log) && (
                  <div className="mt-3 max-h-52 overflow-y-auto bg-zinc-950 border border-zinc-800 rounded-sm p-3 space-y-1" data-testid="fight-log">
                    {(node.result.log || node.result.fight?.log).map((l, i) => <p key={i} className="text-xs text-zinc-400">{l}</p>)}
                  </div>
                )}
                <RewardLine rewards={node.result.rewards} />
                {node.result.checkpoint_reached && node.result.kind !== "finale" && (
                  <p data-testid="result-checkpoint-secured" className="text-xs text-amber-300 mt-3 flex items-center gap-1.5">
                    <Flag size={13} /> Rewards banked — everything you earned since your last bank is now secured.
                  </p>
                )}
                {node.result.escrowed && (
                  <p data-testid="result-escrowed" className="text-xs text-slate-300/90 mt-3 flex items-center gap-1.5"
                    title="These rewards are held in escrow until you reach the next Bank Reward point.">
                    <HandCoins size={13} /> Held in escrow — reach {node.result.next_checkpoint?.title ? <b className="text-amber-300">{node.result.next_checkpoint.title}</b> : "the next Bank Reward point"} to bank these rewards. Leave before then and they're forfeited.
                  </p>
                )}
                {node.result.node_cleared && <p className="text-xs text-emerald-400 mt-3">✓ Location conquered.</p>}
                {node.result.won === false && <p className="text-xs text-rose-400 mt-3">Defeated — your gladiator is wounded. Heal at camp and try again.</p>}
                </>
                )}
                <button data-testid="close-node-btn" onClick={() => { setNode(null); setBoostDone(null); }} className={btn("ghost") + " mt-4 w-full"}>Continue</button>
              </div>
            )}

            {busy && !node.prep && !node.encounter && !node.result && <Loader />}
          </div>
        )}
      </Modal>

      {/* Camp modal */}
      <Modal open={campOpen} onClose={() => setCampOpen(false)} title="Make Camp" wide>
        <div className="space-y-2" data-testid="camp-modal">
          <div data-testid="camp-denarii" className="flex items-center justify-between mb-2 px-3 py-2 border border-slate-600/40 bg-slate-800/20 rounded-sm">
            <span className="text-[11px] uppercase tracking-widest text-zinc-500">Your Denarii</span>
            <span className="flex items-center gap-1.5 text-sm font-cinzel text-slate-200"><HandCoins size={14} /> {data.denarii}</span>
          </div>
          <p className="text-[10px] text-amber-400/80 mb-3 text-center">
            Complete prolusio scrimmages to earn extra denarii!
          </p>
          {exp?.rested ? (
            <p className="text-xs text-zinc-500 mb-1 border-l-2 border-zinc-700 pl-2">You've already rested this leg — press on to the next location to make camp again. You can still buy supplies below.</p>
          ) : (
            <>
              <p className="text-xs text-zinc-500 mb-1">One rest activity before you press on. Stamina only recovers over time (+5/hr).</p>
              {data.camp_activities.map((a) => (
                <button key={a.id} data-testid={`camp-${a.id}`} disabled={busy} onClick={() => doCamp(a.id)}
                  className="w-full text-left px-4 py-3 border border-zinc-800 bg-zinc-900/40 hover:border-amber-600 rounded-sm transition-colors">
                  <div className="text-sm text-zinc-100 flex items-center justify-between">
                    {a.label}{a.effective_cost ? <span className="text-[11px] text-slate-300 flex items-center gap-0.5"><HandCoins size={11} /> {a.effective_cost} Denarii</span> : null}
                  </div>
                  <div className="text-xs text-zinc-500">{a.desc}</div>
                </button>
              ))}
            </>
          )}
          <div className="pt-3 mt-1 border-t border-zinc-800">
            <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5"><HandCoins size={12} /> Quartermaster Supplies <span className="text-zinc-600 normal-case tracking-normal">· buy &amp; use anytime</span></div>
            {(data.consumables || []).map((c) => {
              // Check if player already has a whetstone buff active
              const hasWhetstoneActive = c.id === 'whetstone' && 
                (data.expedition?.buffs || []).some(b => b.type === 'power' && b.source === 'whetstone');
              
              return (
                <button 
                  key={c.id} 
                  data-testid={`consumable-${c.id}`} 
                  disabled={busy || data.denarii < c.effective_cost || hasWhetstoneActive} 
                  onClick={() => buyConsumable(c.id)}
                  className="w-full text-left px-4 py-3 mb-2 border border-zinc-800 bg-zinc-900/40 hover:border-amber-600 rounded-sm transition-colors disabled:opacity-40 disabled:hover:border-zinc-800">
                  <div className="text-sm text-zinc-100 flex items-center justify-between">
                    {c.name}<span className="text-[11px] text-slate-300 flex items-center gap-0.5"><HandCoins size={11} /> {c.effective_cost} Denarii</span>
                  </div>
                  <div className="text-xs text-zinc-500">
                    {c.desc}
                    {hasWhetstoneActive && <span className="text-amber-400 ml-2">· Already active</span>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </Modal>

      {/* Cancel Province confirmation — always restarts the province; escrow forfeited, banked rewards kept */}
      <Modal open={confirmAbandon} onClose={() => setConfirmAbandon(false)} title="Abandon Expedition & Restart Province?">
        <div className="space-y-4" data-testid="confirm-abandon-modal">
          <p className="text-sm text-rose-300/90 leading-relaxed border-l-2 border-rose-800/50 pl-3">
            This <b>restarts the province from the beginning</b> — Bank Reward points are NOT progress saves. Rewards still held in escrow{exp?.escrow_preview?.count > 0 ? <> (<b>+{exp.escrow_preview.fame || 0} Fame, +{exp.escrow_preview.denarii || 0} Denarii{exp.escrow_preview.collectibles ? `, ${exp.escrow_preview.collectibles} relic(s)` : ""}</b>)</> : ""} are <b className="text-rose-300">forfeited</b>. Anything you already <b className="text-amber-300">banked stays yours</b>. Your squad is freed to begin anew.
          </p>
          <div className="flex gap-2 justify-end">
            <button data-testid="confirm-abandon-cancel" onClick={() => setConfirmAbandon(false)} className="px-3 py-2 text-sm border border-zinc-700 text-zinc-400 hover:border-zinc-500 rounded-sm transition-colors">Keep Going</button>
            <button data-testid="confirm-abandon-go" onClick={abandon} disabled={busy} className="px-3 py-2 text-sm border border-rose-700 text-rose-300 hover:bg-rose-950/30 rounded-sm transition-colors flex items-center gap-2"><Skull size={15} /> Abandon & Restart</button>
          </div>
        </div>
      </Modal>

      {/* Begin Expedition confirmation — lock-in warning */}
      <Modal open={confirmStart} onClose={() => setConfirmStart(false)} title="Commit to the Campaign?">
        <div className="space-y-4" data-testid="confirm-start-modal">
          <p className="text-sm text-zinc-300 leading-relaxed">
            Your squad: <b className="text-amber-300">{squadPicks.map((id) => overview.gladiators.find((g) => g.id === id)?.name).filter(Boolean).join(", ") || "these gladiators"}</b>; will be <b className="text-amber-300">locked into this campaign</b>(PvP is still available). There is no retreating, they march until the province is conquered.
          </p>
          <p className="text-sm text-rose-300/90 leading-relaxed border-l-2 border-rose-800/50 pl-3">
            If you retire <b>any</b> squad member while on expedition, the whole expedition is abandoned and <b>all progress</b> in that area is lost.
          </p>
          <div className="flex gap-2 justify-end">
            <button data-testid="confirm-start-cancel" onClick={() => setConfirmStart(false)} className="px-3 py-2 text-sm border border-zinc-700 text-zinc-400 hover:border-zinc-500 rounded-sm transition-colors">Not yet</button>
            <button data-testid="confirm-start-go" onClick={start} disabled={busy} className={btn() + " flex items-center gap-2"}><Compass size={15} /> March Out</button>
          </div>
        </div>
      </Modal>

      {/* Rest-first prompt — fires when a node is clicked without camping since the last fight */}
      <Modal open={!!restPrompt} onClose={() => setRestPrompt(null)} title="Press On, or Make Camp?">
        <div className="space-y-4" data-testid="rest-prompt-modal">
          <p className="text-sm text-zinc-300 leading-relaxed">
            You haven't made camp since your last encounter. Make camp once now to heal, scout, or prepare — or press on to <b className="text-amber-300">{restPrompt?.title}</b>.
          </p>
          <div className="flex gap-2 justify-end">
            <button data-testid="rest-go-camp" onClick={() => { const n = restPrompt; setRestPrompt(null); setCampOpen(true); }} className={btn("ghost") + " flex items-center gap-2"}><Tent size={15} /> Make Camp</button>
            <button data-testid="rest-press-on" onClick={() => enter(restPrompt)} disabled={busy} className={btn() + " flex items-center gap-2"}><ChevronRight size={15} /> Press On</button>
          </div>
        </div>
      </Modal>

      {/* Gold-spend confirmation for stamina recovery (rest) */}
      <Modal open={confirmRest} onClose={() => setConfirmRest(false)} title="Recover Stamina with Gold?">
        <div className="space-y-4" data-testid="rest-gold-confirm-modal">
          <p className="text-sm text-zinc-300 leading-relaxed">
            Resting costs <b className="text-amber-300">{data?.stamina_recover?.cost} Gold</b> (your premium currency — <b>not</b> Denarii) to restore <b className="text-amber-300">+{data?.stamina_recover?.amount} Stamina</b>. Continue?
          </p>
          <label data-testid="rest-dont-ask" className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer select-none">
            <input type="checkbox" checked={restModalDontAsk} onChange={(e) => setRestModalDontAsk(e.target.checked)} className="accent-amber-500 w-4 h-4" />
            Don't show this again
          </label>
          <div className="flex gap-2 justify-end">
            <button data-testid="rest-cancel" onClick={() => setConfirmRest(false)} className={btn("ghost")}>Cancel</button>
            <button data-testid="rest-confirm" onClick={confirmRestNow} disabled={busy} className={btn() + " flex items-center gap-2"}><Sparkles size={15} /> Spend {data?.stamina_recover?.cost} Gold</button>
          </div>
        </div>
      </Modal>

      {/* Repeatable capstone gear reward for each completion of Gaul/Germania/Greece */}
      <Modal open={legOpen} onClose={() => setLegOpen(false)} title={`Spoils of ${overview.pending_legendary_reward?.province_name || "Conquest"}`} wide>
        {(() => {
          const pr = overview.pending_legendary_reward || {};
          const rl = pr.rarity || "legendary";
          const rlab = rl.charAt(0).toUpperCase() + rl.slice(1);
          return (
        <div className="space-y-5" data-testid="legendary-reward-modal">
          <p className="text-sm text-zinc-300 leading-relaxed">
            For conquering <span className="text-amber-300 font-semibold">{pr.province_name}</span> you are granted {(/^[aeiou]/i.test(rlab) ? "an" : "a")} <span className="text-amber-300 font-semibold">{rlab} weapon</span> (for any class you choose) and {(/^[aeiou]/i.test(rlab) ? "an" : "a")} <span className="text-amber-300 font-semibold">{rlab} armor</span>. Roll their affixes below, then claim.
          </p>
          <p data-testid="legendary-onetime-note" className="text-[11px] uppercase tracking-widest text-amber-500/80 border border-amber-700/40 bg-amber-950/20 px-3 py-2 rounded-sm">
            ⚠ One-time reward — granted only for your first {pr.province_name} victory. It will not be offered on future playthroughs.
          </p>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Weapon */}
            <div className="border border-zinc-800 bg-zinc-950 rounded-sm p-4" data-testid="legendary-weapon-panel">
              <h3 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm flex items-center gap-2 mb-3"><Swords size={15} /> {rlab} Weapon</h3>
              <label className="text-[11px] uppercase tracking-widest text-zinc-500">Forge for class</label>
              <select data-testid="legendary-weapon-class" value={legWpnCls} onChange={(e) => setLegWpnCls(e.target.value)}
                className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 mt-1 mb-3 text-zinc-200 focus:border-amber-600 outline-none text-sm">
                <option value="">— choose a class —</option>
                {["Dimachaerus", "Retiarius", "Provocator"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              {legWpn ? <ItemRoll item={legWpn} /> : <p className="text-xs text-zinc-600 italic py-3">Pick a class and roll to forge your {rl} weapon.</p>}
              <button data-testid="legendary-roll-weapon" onClick={() => rollLeg("weapon")} disabled={legBusy || !legWpnCls || (legWpn && legWpnRer <= 0)}
                className={btn() + " w-full mt-3 flex items-center justify-center gap-2 disabled:opacity-50"}>
                <Sparkles size={14} /> {!legWpn ? "Roll Affixes" : legWpnRer > 0 ? `Reroll (${legWpnRer} left)` : "No rerolls left"}
              </button>
            </div>

            {/* Armor */}
            <div className="border border-zinc-800 bg-zinc-950 rounded-sm p-4" data-testid="legendary-armor-panel">
              <h3 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm flex items-center gap-2 mb-3"><Shield size={15} /> {rlab} Armor</h3>
              {legArm ? <ItemRoll item={legArm} /> : <p className="text-xs text-zinc-600 italic py-3">Roll to forge your {rl} armor.</p>}
              <button data-testid="legendary-roll-armor" onClick={() => rollLeg("armor")} disabled={legBusy || (legArm && legArmRer <= 0)}
                className={btn() + " w-full mt-3 flex items-center justify-center gap-2 disabled:opacity-50"}>
                <Sparkles size={14} /> {!legArm ? "Roll Affixes" : legArmRer > 0 ? `Reroll (${legArmRer} left)` : "No rerolls left"}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 pt-1">
            <span className="text-[11px] text-zinc-600">Roll both, then claim. Rerolls are limited — keep the roll you like.</span>
            <button data-testid="legendary-claim-btn" onClick={claimLeg} disabled={legBusy || !legWpn || !legArm}
              className={btn() + " flex items-center gap-2 disabled:opacity-50"}>
              <Crown size={15} /> {legBusy ? "Claiming…" : "Claim Both"}
            </button>
          </div>
        </div>
          );
        })()}
      </Modal>

      {/* Campaign Stamina Potion Confirmation Modal */}
      <Modal open={showPotionConfirm} onClose={() => setShowPotionConfirm(false)} title="Use Campaign Stamina Potion">
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-4 bg-amber-950/20 border border-amber-800/40 rounded-sm">
            <img src="/campaign-stamina-v2.png" alt="Campaign Stamina Potion" className="w-12 h-12 object-contain" />
            <div>
              <h3 className="font-cinzel text-amber-400 text-sm">Campaign Stamina Potion</h3>
              <p className="text-xs text-zinc-400">Restore +50 stamina to your expedition</p>
            </div>
          </div>

          <div>
            <p className="text-sm text-zinc-300 mb-2">
              This will consume 1 Campaign Stamina Potion to restore <span className="text-amber-400 font-semibold">+50 stamina</span> to your active expedition.
            </p>
            {exp && typeof exp.stamina !== 'undefined' && (
              <div className="p-3 bg-zinc-900/50 border border-zinc-700/40 rounded-sm">
                <p className="text-xs text-zinc-400">
                  Current stamina: <span className="text-amber-400 font-semibold">{Math.floor(exp.stamina)}/{exp.stamina_max}</span>
                </p>
                <p className="text-xs text-zinc-400 mt-1">
                  After use: <span className="text-amber-400 font-semibold">{Math.min(exp.stamina_max, Math.floor(exp.stamina) + 50)}/{exp.stamina_max}</span>
                </p>
              </div>
            )}
          </div>

          <div className="text-xs text-zinc-500">
            <p>• Remaining potions: <span className="text-amber-400">{potionCount - 1}</span></p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => setShowPotionConfirm(false)}
              className={btn() + " flex-1 bg-zinc-800 hover:bg-zinc-700"}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              onClick={useStaminaPotion}
              className={btn() + " flex-1 bg-amber-600 hover:bg-amber-500"}
              disabled={busy}
            >
              {busy ? "Using..." : "Use Potion"}
            </button>
          </div>
        </div>
      </Modal>

    </div>
  );
}


function ItemRoll({ item }) {
  const catColor = { offensive: "text-rose-300", defensive: "text-sky-300", utility: "text-emerald-300" };
  const rarMeta = {
    rare: { label: "Rare", text: "text-sky-300", border: "border-sky-600/50", bg: "bg-sky-950/10" },
    elite: { label: "Elite", text: "text-fuchsia-300", border: "border-fuchsia-600/50", bg: "bg-fuchsia-950/10" },
    legendary: { label: "Legendary", text: "text-amber-300", border: "border-amber-600/50", bg: "bg-amber-950/10" }}[item.rarity] || { label: item.rarity, text: "text-amber-300", border: "border-amber-600/50", bg: "bg-amber-950/10" };
  return (
    <div data-testid="legendary-item-roll" className={`border ${rarMeta.border} ${rarMeta.bg} rounded-sm p-3`}>
      <div className={`font-cinzel text-sm ${rarMeta.text}`}>{item.name}</div>
      <div className={`text-[10px] uppercase tracking-widest ${rarMeta.text} opacity-70 mb-2`}>{rarMeta.label} {item.kind}{item.cls ? ` · ${item.cls}` : ""}</div>
      {item.base_mods && Object.keys(item.base_mods).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {Object.entries(item.base_mods).map(([k, v]) => (
            <span key={k} className="text-[11px] text-zinc-300 border border-zinc-700 bg-zinc-900/60 px-1.5 py-0.5 rounded-sm">+{v} {k.charAt(0).toUpperCase() + k.slice(1)}</span>
          ))}
        </div>
      )}
      <div className="space-y-1">
        {(item.affixes || []).map((a, i) => (
          <div key={i} className={`text-xs ${catColor[a.cat] || "text-zinc-300"}`}>◈ {a.display}</div>
        ))}
      </div>
    </div>
  );
}
